import { canCompanyWide, licenseBannerText } from '@epc/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import { Building2, Lock, ShieldAlert } from 'lucide-react';
import { Logo } from '../layout/Logo';
import { api } from '../lib/api';
import { useMe } from '../lib/session';
import { Button } from '../ui/button';

/**
 * Плашка о состоянии лицензии компании (ЛЦ-03): заканчивается, льготный период, только чтение.
 * Видна всем сотрудникам — чтобы «изменения закрыты» не было неожиданностью; ссылка на страницу
 * лицензии — тем, кто меняет настройки компании.
 */
export function LicenseBanner() {
  const me = useMe();
  const lic = me.license;
  if (!lic) return null;
  const text = licenseBannerText(lic);
  if (!text) return null;
  const danger = lic.state !== 'expiring';
  const canOpen = canCompanyWide(me.access, 'admin.settings') || !!me.tenant?.isOwner;
  return (
    <div
      role="status"
      className={clsx(
        'border-b print:hidden',
        danger ? 'border-danger/30 bg-danger-soft text-ink' : 'border-warning/30 bg-warning-soft text-ink',
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2 md:px-8">
        {danger ? (
          <Lock className="size-5 shrink-0 text-danger" aria-hidden />
        ) : (
          <ShieldAlert className="size-5 shrink-0 text-warning" aria-hidden />
        )}
        <div className="min-w-0 flex-1 basis-[220px] text-[13px] leading-5">
          <span className="font-semibold">{text}</span>
          {canOpen ? null : (
            <span className="ml-1.5 text-ink-2">Продлением занимается администратор вашей компании.</span>
          )}
        </div>
        {canOpen ? (
          <Link
            to="/admin/license"
            className="inline-flex h-11 items-center px-2 text-[13px] font-medium underline-offset-[3px] hover:underline md:h-8"
          >
            Открыть лицензию
          </Link>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Доступ компании приостановлен владельцем портала (ЛЦ-02): вместо портала — объяснение, переключение на
 * другую компанию и выход. API в этом состоянии отвечает отказом на всё, кроме входа и уведомлений.
 */
export function SuspendedScreen() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const others = me.tenants.filter((t) => t.id !== me.tenant?.id);

  async function logout() {
    await api('/auth/logout', { method: 'POST' }).catch(() => {});
    qc.clear();
    navigate({ to: '/login' });
  }

  async function switchTenant(id: string) {
    const next = await api('/auth/tenant', { body: { tenantId: id } });
    qc.clear();
    qc.setQueryData(['me'], next);
    navigate({ to: '/' });
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-surface-2 px-5 py-10">
      <div className="w-full max-w-[460px] rounded-lg border border-line bg-surface p-6 md:p-8">
        <Logo height={24} className="text-ink" />
        <h1 className="mt-6 text-[22px] leading-7 font-semibold text-ink">Доступ компании приостановлен</h1>
        <p className="mt-3 text-[14px] leading-5 text-ink-2">
          Владелец портала приостановил доступ компании «{me.tenant?.name}»
          {me.license?.suspendedReason ? `: ${me.license.suspendedReason}` : ''}. Данные сохранены и будут
          доступны, когда доступ возобновят. По вопросам лицензии обратитесь к администратору вашей компании
          или к владельцу портала.
        </p>
        <div className="mt-6 flex flex-col gap-2">
          {me.user.isPlatformAdmin ? (
            <Link
              to="/platform"
              className="inline-flex h-11 items-center justify-center rounded-md bg-accent px-4 text-[14px] font-semibold text-on-accent md:h-10"
            >
              Кабинет владельца портала
            </Link>
          ) : null}
          {others.map((t) => (
            <Button
              key={t.id}
              variant="secondary"
              icon={<Building2 className="size-4" />}
              onClick={() => void switchTenant(t.id)}
            >
              Перейти в «{t.name}»
            </Button>
          ))}
          <Button variant={others.length ? 'ghost' : 'primary'} onClick={() => void logout()}>
            Выйти
          </Button>
        </div>
      </div>
    </div>
  );
}
